// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file RatingDots — numbered rating scale
 * @version 0.1.2
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import styles from './RatingDots.module.css';

/*
 * RatingDots — a horizontal rating-scale reference: a row of numbered dots
 * (1…N) each captioned beneath. Used on the "Using the rating scale"
 * instruction screen and question intros.
 *
 *   <RatingDots items={['Never','Rarely','Sometimes','Half','Usually','Often','Always']} />
 *
 * `theme` is a variant, not a global mode — pick it per instance like any other
 * variant:
 *   light (default) — navy dot + white number, navy captions, no panel.
 *   dark            — translucent teal-grey dot + off-white number, white
 *                     captions, inside a translucent panel with hairline column
 *                     dividers.
 *
 * The dot count is derived from `items.length` (typically 5 or 7). At narrow
 * widths the row restacks into a two-column grid.
 */

export type RatingDotsTheme = 'light' | 'dark';

export interface RatingDotsProps extends HTMLAttributes<HTMLDivElement> {
  /** Captions under each dot, left → right. Count = items.length (5 or 7). */
  items: readonly string[];
  /** Colour treatment. Default `light`. A variant, not a runtime theme mode. */
  theme?: RatingDotsTheme;
  /** Render root as a different element (default `div`). */
  as?: ElementType;
  /** Accessible name for the scale. */
  'aria-label'?: string;
}

export const RatingDots = forwardRef<HTMLDivElement, RatingDotsProps>(
  function RatingDots(
    { items, theme = 'light', as, className, 'aria-label': ariaLabel = 'Rating scale', ...rest },
    ref,
  ) {
    const Component = (as ?? 'div') as ElementType;
    const rootClass = [styles.root, styles[theme], className].filter(Boolean).join(' ');
    return (
      <Component ref={ref} className={rootClass} role="list" aria-label={ariaLabel} {...rest}>
        {items.map((label, i) => (
          <div key={`${i}-${label}`} className={styles.col} role="listitem">
            <span className={styles.dot}><span className={styles.number}>{i + 1}</span></span>
            <span className={styles.caption}>{label}</span>
          </div>
        ))}
      </Component>
    );
  },
);
